import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  IconButton,
  CircularProgress,
  Alert,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useVersionManager } from './hooks/useVersionManager';
import {
  TemplateInfoCard,
  VersionList,
  NewVersionDialog,
  PromoteVersionDialog,
  RollbackVersionDialog,
  VersionStatusDialog,
} from './components';

const VersionManager = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { templateId } = useParams();
  const { tenant } = useAuth();

  // Use custom hook for version management
  const {
    template,
    versions,
    policySummaryByVersion,
    loading,
    error,
    success,
    actionLoading,
    createNewVersion,
    promoteVersion,
    updateVersionStatus,
    rollbackToVersion,
    setError,
    setSuccess,
  } = useVersionManager(templateId, tenant);

  // UI State
  const [selectedVersions, setSelectedVersions] = useState([]);
  const [comparisonMode, setComparisonMode] = useState(false);
  const [newVersionDialog, setNewVersionDialog] = useState(false);
  const [promoteDialog, setPromoteDialog] = useState(false);
  const [statusDialog, setStatusDialog] = useState(false);
  const [rollbackDialog, setRollbackDialog] = useState(false);
  const [selectedVersionForAction, setSelectedVersionForAction] =
    useState(null);

  // Event handlers
  const handleVersionSelect = version => {
    if (selectedVersions.includes(version)) {
      setSelectedVersions(selectedVersions.filter(v => v !== version));
    } else if (selectedVersions.length < 2) {
      setSelectedVersions([...selectedVersions, version]);
    }
  };

  const handleCreateNewVersion = () => {
    setNewVersionDialog(true);
  };

  const handleToggleComparison = () => {
    setComparisonMode(!comparisonMode);
  };

  const handlePromoteVersion = version => {
    setSelectedVersionForAction(version);
    setPromoteDialog(true);
  };

  const handleChangeStatus = version => {
    setSelectedVersionForAction(version);
    setStatusDialog(true);
  };

  const handleUpdateStatus = async (versionNumber, statusData) => {
    return await updateVersionStatus(versionNumber, statusData);
  };

  const handleRollbackVersion = version => {
    setSelectedVersionForAction(version);
    setRollbackDialog(true);
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
        <IconButton
          onClick={() =>
            navigate(location.state?.from || '/templates', { replace: false })
          }
          sx={{ mr: 2 }}
        >
          <ArrowBackIcon />
        </IconButton>
        <Box>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 600 }}>
            Version Manager
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            {template?.name || 'Template'} - Manage versions and track changes
          </Typography>
        </Box>
      </Box>

      {/* Success/Error Messages */}
      {success && (
        <Alert
          severity="success"
          sx={{ mb: 3 }}
          onClose={() => setSuccess(null)}
        >
          {success}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Template Info Card */}
      <TemplateInfoCard
        template={template}
        templateId={templateId}
        comparisonMode={comparisonMode}
        selectedVersions={selectedVersions}
        onCreateNewVersion={handleCreateNewVersion}
        onToggleComparison={handleToggleComparison}
      />

      {/* Versions List */}
      <VersionList
        versions={versions}
        selectedVersions={selectedVersions}
        templateId={templateId}
        template={template}
        policySummaryByVersion={policySummaryByVersion}
        onVersionSelect={handleVersionSelect}
        onPromoteVersion={handlePromoteVersion}
        onRollbackVersion={handleRollbackVersion}
        onChangeStatus={handleChangeStatus}
      />

      {/* Dialogs */}
      <NewVersionDialog
        open={newVersionDialog}
        onClose={() => setNewVersionDialog(false)}
        onSubmit={createNewVersion}
        loading={actionLoading}
      />

      <PromoteVersionDialog
        open={promoteDialog}
        onClose={() => setPromoteDialog(false)}
        onConfirm={promoteVersion}
        version={selectedVersionForAction}
        loading={actionLoading}
      />

      <VersionStatusDialog
        open={statusDialog}
        onClose={() => setStatusDialog(false)}
        onConfirm={handleUpdateStatus}
        version={selectedVersionForAction}
        loading={actionLoading}
      />

      <RollbackVersionDialog
        open={rollbackDialog}
        onClose={() => setRollbackDialog(false)}
        onConfirm={rollbackToVersion}
        version={selectedVersionForAction}
        loading={actionLoading}
      />
    </Container>
  );
};

export default VersionManager;
